module Client.IllyAdmin.Actions.Loggers.Section

open Client
open Fable.React
open Fable.React.Props
open Shared.IllyAdmin.Loggers
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open SafeStack.Client
open SafeStack.Client.HTMLIdentifier

type SectionId = HTMLIdentifier

module Row =
    type RowId = HTMLIdentifier

    type Model =
        {
            Id: RowId
            Data: LoggerMetadata
            SelectedForGeneration: Check.Model
            Visible: bool
        }

    type Msg =
        | SelectedForGenerationMsg of Check.Msg

    module Init =
        let selectedForGeneration id value =
            { (Check.CustomSettings.defaultOptions ()) with RenderInline = true }
            |> Check.init (sprintf "%s-selected-for-generation" (HTMLIdentifier.toString id)) "Include in Config" false value 

    let setChecked isChecked model =
        { model with SelectedForGeneration = Init.selectedForGeneration model.Id isChecked }

    let setVisible isVisible model =
        { model with Visible = isVisible }

    let init sectionId rowId metadata =
        let id = sprintf "%s-row-%s" (HTMLIdentifier.toString sectionId) rowId |> HTMLIdentifier.ofStringOrFail
        {
            Id = id
            Data = metadata
            SelectedForGeneration = Init.selectedForGeneration id false
            Visible = true
        }

    let update msg model =
        match msg with
        | SelectedForGenerationMsg msg ->
            let m, cmd = Check.update msg model.SelectedForGeneration
            { model with SelectedForGeneration = m }, cmd

type Model =
    {
        Id: SectionId
        Header: string
        GenerateConfigDisabled: bool
        SelectAll: Check.Model
        Rows: Row.Model list
    }

type Msg =
    | RowMsg of Row.RowId * Row.Msg
    | SelectAllMsg of Check.Msg
    | GenerateConfigMsg

type Events =
    | OpenOverlay of LoggerMetadata list

module Init =
    let selectAll sectionId value =
        Check.CustomSettings.defaultOptions ()
        |> Check.init (sprintf "%s-select-all" (HTMLIdentifier.toString sectionId)) "Select All" false value

let init sectionId header (metadata: LoggerMetadata list) =
    let id = sprintf "logger-section-%s" sectionId |> HTMLIdentifier.ofStringOrFail
    {
        Id = id
        Header = header
        GenerateConfigDisabled = true
        SelectAll = Init.selectAll id false
        Rows = metadata |> List.map (fun m -> Row.init id m.Key m )
    }

let hasVisibleRows =
    List.exists (fun (r: Row.Model) -> r.Visible)

let allRowsSelected =
    List.exists (fun (r: Row.Model) -> Check.toBool r.SelectedForGeneration = false) >> not

let anyRowsSelected =
    List.exists (fun (r: Row.Model) -> Check.toBool r.SelectedForGeneration)

let filterRows searchTerm model =
    match searchTerm with
    | Some (s: string) ->
        let ss = s.ToLower()
        let searchIn (str: string) =
            str.ToLower().Contains(ss)

        let newRows =
            model.Rows |> List.map (fun r ->
                Row.setVisible (searchIn r.Data.Key || searchIn r.Data.Description || searchIn r.Data.DisplayName) r
            )

        { model with Rows = newRows }
    | None ->
        { model with Rows = model.Rows |> List.map (Row.setVisible true) }

module Dispatch =
    let rowMsg row msg model =
        let m,cmd =
            model.Rows
            |> List.find (fun r -> r.Id = row)
            |> Row.update msg

        let newRows = model.Rows |> ListFunctions.replace (fun r -> r.Id) (fun _ -> m) row

        { model with
            Rows = newRows
            SelectAll = Init.selectAll model.Id (allRowsSelected newRows)
            GenerateConfigDisabled = not <| anyRowsSelected newRows
        }, cmd |> SideEffect.Cmd

    let selectAllMsg msg model =
        let m,cmd = Check.update msg model.SelectAll
        let isChecked = Check.toBool m

        let newRows =
            if isChecked <> Check.toBool model.SelectAll then
                model.Rows
                |> List.map (fun r ->
                    { r with SelectedForGeneration = Row.Init.selectedForGeneration r.Id isChecked }
                ) 
            else
                model.Rows

        { model with
            Rows = newRows
            SelectAll = m
            GenerateConfigDisabled = not <| anyRowsSelected newRows
        }, cmd |> SideEffect.Cmd

let update msg model =
    match msg with
    | RowMsg (row, rMsg) -> Dispatch.rowMsg row rMsg model
    | SelectAllMsg saMsg -> Dispatch.selectAllMsg saMsg model
    | GenerateConfigMsg ->
        model, OpenOverlay (model.Rows |> List.choose (fun r -> if Check.toBool r.SelectedForGeneration then Some r.Data else None)) |> SideEffect.Event 

let view loading model dispatch =
    [
        if hasVisibleRows model.Rows then
            yield section [
                Id (HTMLIdentifier.toString model.Id)
                Class "marginBottom-2"
            ] [
                let tooltip =
                    if model.GenerateConfigDisabled then Tooltip.create [ str "Select some loggers below to generate a config for them" ]
                    else id

                jQueryUI.simpleTable
                    "No loggers available"
                    [
                        Some [
                            str "Name"
                        ] ===> fun (r: Row.Model) -> [
                            str r.Data.DisplayName
                        ]

                        Some [ str "Key" ] ===> fun (r: Row.Model) -> [ str r.Data.Key ]

                        Some [ str "Description" ] ===> fun (r: Row.Model) -> [ str r.Data.Description ]

                        Some [
                            if not loading then
                                yield div [ Class "marginBottom-1" ] [
                                    UI.buttonWithDisabled model.GenerateConfigDisabled "Generate Config" GenerateConfigMsg dispatch
                                ]
                                |> tooltip
                            yield Check.checkInput loading model.SelectAll (SelectAllMsg >> dispatch)
                        ] ===> fun (r: Row.Model) -> [
                            Check.checkInput loading r.SelectedForGeneration ((fun msg -> r.Id, Row.SelectedForGenerationMsg msg) >> RowMsg >> dispatch)
                        ]
                    ]
                    loading
                    model.Rows
            ]
    ]